import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  getAllOffers,
  getOfferByID,
  createOffer,
  updateOffer,
  OfferImageUpload,
} from "./services/offer.services";
import { clearOfferDetails } from "./offerSlice";
import { useNavigate, useLocation } from "react-router-dom";
import ShadowCard from "../../components/common/ShadowCard";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import FormTextArea from "../../components/formFields/FormTextArea";
import { slugify, getFile } from "../../_helper/helper";
import FormImageUpload from "../../components/formFields/FormImageUpload";

const CreateOffer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<any>(null);
  const OfferID = location.state?.OfferID;
  const OfferDetail = useAppSelector((state) => state.offer.OfferDetail);
  const loading = useAppSelector((state) => state.offer.formLoading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (OfferID) {
      dispatch(getOfferByID(OfferID));
    }
    return () => {
      dispatch(clearOfferDetails());
    };
  }, [OfferID, dispatch]);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [OfferDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      ShortDescription: values.ShortDescription ?? "",
      LongDescription: values.LongDescription ?? "",
      URL: values.URL ?? "",
      MetaTitle: values.MetaTitle ?? "",
      MetaKeywords: values.MetaKeywords ?? "",
      MetaDescription: values.MetaDescription ?? "",
      TermsAndCondition: values.TermsAndCondition ?? "",
      IsDisplayHome: values.IsDisplayHome ?? false,
      IsActive: values.IsActive === 1,
    };

    const ImageData = new FormData();
    const Image =
      values?.Images && typeof values?.Images !== "string"
        ? getFile(values?.Images)
        : "";

    try {
      if (OfferID) {
        const updatePayload = {
          ...formData,
          ID: OfferID,
        };
        const response = await dispatch(updateOffer(updatePayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.Images && typeof values?.Images !== "string") {
            ImageData.append("Images", Image);
            ImageData.append("ID", OfferID.toString());
            await dispatch(OfferImageUpload(ImageData));
          }
          navigate("/offer");
          dispatch(getAllOffers());
          dispatch(clearOfferDetails());
        }
      } else {
        const response = await dispatch(createOffer(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Images &&
            typeof values?.Images !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Images", Image);
            ImageData.append("ID", response?.payload?.Data?.ID.toString());
            await dispatch(OfferImageUpload(ImageData));
          }
          navigate("/offer");
          dispatch(getAllOffers());
          dispatch(clearOfferDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={OfferDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 20 }}
                  cols={[{ width: "50%" }, { width: "50%" }]}
                >
                  <GridLayoutItem colSpan={2}>
                    <Typography.h4>
                      {OfferID ? "Update Offer" : "Create Offer"}
                    </Typography.h4>
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="New Year Mega Offer"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      onChange={(e: any) =>
                        handleCreateSlug(e, formRenderProps)
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="URL"
                      label="URL"
                      placeholder="e.g., https://yourstore.com/offer"
                      component={FormTextField}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Brief details about the offer"
                      component={FormTextArea}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="LongDescription"
                      label="Long Description"
                      component={FormRichTextField}
                      style={{ height: 200 }}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="TermsAndCondition"
                      label="Terms and Conditions"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="Images"
                      label="Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <div
                      style={{
                        width: "100%",
                        margin: "18px 0px 0px",
                        padding: "0px 10px 8px",
                        fontWeight: 700,
                        background: "whitesmoke",
                        borderRadius: 5,
                        paddingTop: 1,
                        marginTop: 35,
                        height: 48,
                      }}
                    >
                      <Field
                        name="IsDisplayHome"
                        label="Display on Home Page"
                        component={FormCheckbox}
                      />
                    </div>

                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => ({
                        value: status?.value,
                        label: status?.label,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title for SEO"
                      component={FormTextField}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="MetaKeywords"
                      label="Meta Keywords"
                      placeholder="Keyword1, Keyword2"
                      component={FormTextField}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      placeholder="Meta description for SEO"
                      component={FormTextArea}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      marginTop: "20px",
                      textAlign: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={OfferID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/offer")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateOffer;
